import type {
  Booking,
  BookingStatus,
  CompanyDocStatus,
  CompanyState,
  Driver,
  DriverDocStatus,
  DriverDocType,
  Invoice,
  Listing,
  Plan,
  PublishStatus,
  SubscriptionStatus,
  Unit,
  UnitStatus,
} from "../types/company";
import type { BadgeTone, CompanyDocumentKey, CompanyVerification } from "../types/vtc";

/**
 * Dummy data for the company area.
 * Swap for API calls later — pages read it through useCompany(), so their
 * props and markup stay the same. UI copy lives in src/i18n; free text written
 * by companies or drivers (messages, notes, descriptions) isn't translated.
 */

/** Demo "today", so the data always lines up. Replace with today() for real data. */
export const TODAY = "2026-10-05";

export const ENERGIES = ["Electric", "Hybrid", "Plug-in hybrid", "Petrol", "Diesel"];
export const TRANSMISSIONS = ["Automatic", "Manual"];
export const CATEGORIES = ["Sedan", "Premium sedan", "Estate", "SUV", "Van"];

/** Labels in t.company.docTypes. */
export const DRIVER_DOC_TYPES: DriverDocType[] = ["vtc", "licence", "id", "address"];

/** Names and features in t.company.plans. */
export const PLANS: Plan[] = [
  { id: "free", price: 0, listingLimit: 3 },
  { id: "pro", price: 29, listingLimit: 10 },
  { id: "business", price: 79, listingLimit: 30 },
  { id: "fleet", price: 149, listingLimit: 100 },
];

export const URGENT_OPTIONS = [
  { days: 7, price: 9.9 },
  { days: 30, price: 29.9 },
];

/** Requests still waiting on the company. */
export const OPEN_STATUSES: BookingStatus[] = ["pending", "documents_required"];
/** Statuses that hold a physical vehicle. */
export const BLOCKING_STATUSES: BookingStatus[] = ["confirmed", "active"];
export const FINAL_STATUSES: BookingStatus[] = [
  "completed",
  "declined",
  "cancelled",
  "expired",
];

/* ---------- Badge tones (labels are in t.company.status) ---------- */

export const STATUS_TONES = {
  booking: {
    pending: "amber",
    documents_required: "amber",
    confirmed: "blue",
    active: "green",
    completed: "neutral",
    declined: "red",
    cancelled: "neutral",
    expired: "neutral",
  } satisfies Record<BookingStatus, BadgeTone>,
  publish: {
    draft: "neutral",
    published: "green",
    paused: "amber",
    moderated: "red",
    archived: "neutral",
  } satisfies Record<PublishStatus, BadgeTone>,
  unit: {
    active: "green",
    maintenance: "amber",
    retired: "neutral",
  } satisfies Record<UnitStatus, BadgeTone>,
  driverDoc: {
    approved: "green",
    pending: "amber",
    missing: "red",
    expired: "red",
  } satisfies Record<DriverDocStatus, BadgeTone>,
  subscription: {
    trial: "blue",
    active: "green",
    past_due: "red",
    cancelled: "neutral",
    expired: "neutral",
  } satisfies Record<SubscriptionStatus, BadgeTone>,
  verification: {
    incomplete: "neutral",
    pending: "amber",
    verified: "green",
    rejected: "red",
  } satisfies Record<CompanyVerification, BadgeTone>,
  companyDoc: {
    approved: "green",
    pending: "amber",
    rejected: "red",
    missing: "red",
  } satisfies Record<CompanyDocStatus, BadgeTone>,
  driver: {
    verified: "green",
    pending: "amber",
  } satisfies Record<Driver["status"], BadgeTone>,
  invoice: {
    Paid: "green",
    Failed: "red",
  } satisfies Record<Invoice["status"], BadgeTone>,
} as const;

export const COMPANY_DOC_KEYS: CompanyDocumentKey[] = ["kbis", "insurance", "licence"];

/* ---------- Demo company: VTC Mobility Paris ---------- */

const BASE_LISTING = {
  year: 2024,
  street: "",
  postalCode: "",
  country: "France",
  seats: 5,
  minDays: 7,
  kmIncluded: 1500,
  extraKm: 0.12,
  insurance: "All-risk insurance included, €1,000 excess.",
  maintenance: "Servicing and tyres included. Replacement vehicle within 48 h.",
  requiredDocs: ["vtc", "licence", "id", "address"] as DriverDocType[],
  photos: [true, true, true, false, false],
  description: "",
};

const LISTINGS: Listing[] = [
  { ...BASE_LISTING, id: "l1", brand: "Toyota", model: "Corolla Touring Sports", version: "Hybrid 140 Design", energy: "Hybrid", transmission: "Automatic", category: "Estate", city: "Paris", colour: "Grey", mileage: 32000, weekly: 320, monthly: 1190, deposit: 1500, publish: "published", urgentUntil: "2026-10-09" },
  { ...BASE_LISTING, id: "l2", brand: "Hyundai", model: "Ioniq 5", version: "77 kWh Executive", energy: "Electric", transmission: "Automatic", category: "SUV", city: "Saint-Denis", colour: "White", mileage: 18000, weekly: 390, monthly: 1450, deposit: 2000, publish: "published" },
  { ...BASE_LISTING, id: "l3", brand: "Tesla", model: "Model 3", version: "Long Range", energy: "Electric", transmission: "Automatic", category: "Premium sedan", city: "Paris", colour: "Black", mileage: 25000, weekly: 420, monthly: 1590, deposit: 2500, publish: "published" },
  { ...BASE_LISTING, id: "l4", brand: "Toyota", model: "C-HR", version: "Hybrid 2.0 GR Sport", energy: "Hybrid", transmission: "Automatic", category: "SUV", city: "Roissy-CDG", colour: "Blue", mileage: 41000, weekly: 300, monthly: 1120, deposit: 1500, publish: "published" },
  { ...BASE_LISTING, id: "l5", brand: "Kia", model: "Niro EV", version: "64 kWh Active", energy: "Electric", transmission: "Automatic", category: "SUV", city: "Boulogne-Billancourt", colour: "Silver", mileage: 27000, weekly: 340, monthly: 1260, deposit: 1500, publish: "published" },
  { ...BASE_LISTING, id: "l6", brand: "Skoda", model: "Superb", version: "iV Business", energy: "Plug-in hybrid", transmission: "Automatic", category: "Sedan", city: "Paris", colour: "Black", mileage: 12000, weekly: 360, monthly: 1350, deposit: 2000, publish: "draft", photos: [false, false, false, false, false] },
];

/** n physical vehicles coded PREFIX-001…, with plausible French plates. */
function fleet(listingId: string, prefix: string, count: number, kmBase: number): Unit[] {
  return Array.from({ length: count }, (_, i) => ({
    id: `${listingId}-u${i + 1}`,
    listingId,
    code: `${prefix}-${String(i + 1).padStart(3, "0")}`,
    plate: `G${prefix[0]}-${214 + i * 97}-${"KLMPRSTV"[i]}${"BDFHJ"[i % 5]}`,
    mileage: kmBase + i * 3700,
    status: "active",
    blocks: [],
  }));
}

const UNITS: Unit[] = [
  ...fleet("l1", "COR", 8, 21000),
  ...fleet("l2", "ION", 5, 12000),
  ...fleet("l3", "TM3", 6, 18000),
  ...fleet("l4", "CHR", 4, 35000),
  ...fleet("l5", "NIR", 4, 22000),
].map((u) =>
  u.code === "CHR-004"
    ? { ...u, status: "maintenance" as const }
    : u.code === "TM3-005"
      ? { ...u, blocks: [{ start: "2026-10-20", end: "2026-10-24", reason: "Bodywork" }] }
      : u,
);

const approvedDocs = (): Driver["docs"] => [
  { type: "vtc", status: "approved", expiry: "2029-03-01", file: "vtc-card.pdf" },
  { type: "licence", status: "approved", expiry: "2033-06-12", file: "licence.pdf" },
  { type: "id", status: "approved", expiry: "2031-01-20", file: "id.pdf" },
  { type: "address", status: "approved", file: "proof-of-address.pdf" },
];

const withDoc = (type: DriverDocType, doc: Partial<Driver["docs"][number]>) =>
  approvedDocs().map((d) => (d.type === type ? { ...d, ...doc } : d));

const driver = (
  id: string,
  firstName: string,
  lastName: string,
  city: string,
  docs = approvedDocs(),
  licenceYear = 2016,
): Driver => ({
  id,
  firstName,
  lastName,
  city,
  email: `${firstName}.${lastName}`.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "") + "@mail.fr",
  phone: "+33 6 " + id.replace(/\D/g, "").padStart(2, "0") + " 42 18 7" + id.length,
  createdAt: "2026-03-14",
  licenceYear,
  status: "verified",
  docs,
});

const DRIVERS: Driver[] = [
  driver("d1", "Karim", "Benali", "Paris", withDoc("licence", { status: "expired", expiry: "2026-08-31" }), 2015),
  driver("d2", "Mamadou", "Diallo", "Saint-Denis", withDoc("address", { status: "pending" }), 2018),
  driver("d3", "Sofia", "Martins", "Paris", approvedDocs(), 2014),
  driver("d4", "Julien", "Lefèvre", "Créteil", approvedDocs().map((d) => (d.type === "vtc" || d.type === "address" ? { type: d.type, status: "missing" as const } : d)), 2019),
  driver("d5", "Thomas", "Nguyen", "Nanterre"),
  driver("d6", "Yacine", "Amrani", "Paris"),
  driver("d7", "Nadia", "Haddad", "Boulogne-Billancourt"),
  driver("d8", "Lucas", "Moreau", "Paris"),
  driver("d9", "Inès", "Bouzid", "Noisy-le-Grand"),
  driver("d10", "Omar", "Traoré", "Orly"),
];

const termsOf = (listingId: string) => {
  const l = LISTINGS.find((x) => x.id === listingId)!;
  return { weekly: l.weekly, monthly: l.monthly, deposit: l.deposit, kmIncluded: l.kmIncluded };
};

const booking = (
  b: Omit<Booking, "terms" | "messages" | "history"> &
    Partial<Pick<Booking, "messages" | "history">>,
): Booking => ({
  ...b,
  terms: termsOf(b.listingId),
  messages: b.messages ?? [],
  history: b.history ?? [{ status: "pending", at: b.createdAt, by: "Driver" }],
});

const BOOKINGS: Booking[] = [
  booking({
    id: "R-2041", listingId: "l1", driverId: "d1", unitId: null,
    start: "2026-10-12", end: "2026-11-09", createdAt: "2026-09-30T09:12", status: "pending",
    note: "Hello, I'd like to start on the 12th. I can pick the car up in the morning.",
    messages: [{ from: "driver", text: "Hello, is the car available from 8 am on the 12th?", at: "2026-09-30T09:14" }],
  }),
  booking({
    id: "R-2040", listingId: "l2", driverId: "d2", unitId: null,
    start: "2026-10-07", end: "2026-11-04", createdAt: "2026-09-30T08:47", status: "pending",
  }),
  booking({
    id: "R-2038", listingId: "l3", driverId: "d3", unitId: null,
    start: "2026-10-05", end: "2026-11-02", createdAt: "2026-09-29T10:14", status: "pending",
    note: "Experienced Uber Black driver, 4.95 rating.",
  }),
  booking({
    id: "R-2035", listingId: "l1", driverId: "d4", unitId: null,
    start: "2026-10-03", end: "2026-10-31", createdAt: "2026-09-27T19:05", status: "documents_required",
    messages: [
      { from: "company", text: "Please upload a valid VTC card and a recent proof of address.", at: "2026-09-28T10:02" },
      { from: "system", text: "Request moved to “Documents required”.", at: "2026-09-28T10:02" },
    ],
    history: [
      { status: "pending", at: "2026-09-27T19:05", by: "Driver" },
      { status: "documents_required", at: "2026-09-28T10:02", by: "VTC Mobility Paris" },
    ],
  }),
  booking({
    id: "R-2031", listingId: "l1", driverId: "d5", unitId: "l1-u3",
    start: "2026-10-10", end: "2026-11-07", createdAt: "2026-09-24T14:30", status: "confirmed",
    messages: [
      { from: "company", text: "Confirmed! Pick-up at our Paris 11e agency at 9 am.", at: "2026-09-25T09:10" },
      { from: "driver", text: "Perfect, see you on the 10th.", at: "2026-09-25T09:31" },
    ],
    history: [
      { status: "pending", at: "2026-09-24T14:30", by: "Driver" },
      { status: "confirmed", at: "2026-09-25T09:10", by: "VTC Mobility Paris" },
    ],
  }),
  booking({
    id: "R-2012", listingId: "l1", driverId: "d6", unitId: "l1-u1",
    start: "2026-09-15", end: "2026-10-13", createdAt: "2026-09-08T11:20", status: "active",
    history: [
      { status: "pending", at: "2026-09-08T11:20", by: "Driver" },
      { status: "confirmed", at: "2026-09-09T08:45", by: "VTC Mobility Paris" },
      { status: "active", at: "2026-09-15T09:05", by: "VTC Mobility Paris" },
    ],
  }),
  booking({
    id: "R-2027", listingId: "l3", driverId: "d7", unitId: "l3-u2",
    start: "2026-10-15", end: "2026-11-12", createdAt: "2026-09-21T16:48", status: "confirmed",
    history: [
      { status: "pending", at: "2026-09-21T16:48", by: "Driver" },
      { status: "confirmed", at: "2026-09-22T10:15", by: "VTC Mobility Paris" },
    ],
  }),
  booking({
    id: "R-2018", listingId: "l2", driverId: "d8", unitId: "l2-u2",
    start: "2026-09-21", end: "2026-11-02", createdAt: "2026-09-14T09:02", status: "active",
    history: [
      { status: "pending", at: "2026-09-14T09:02", by: "Driver" },
      { status: "confirmed", at: "2026-09-14T15:40", by: "VTC Mobility Paris" },
      { status: "active", at: "2026-09-21T10:00", by: "VTC Mobility Paris" },
    ],
  }),
  booking({
    id: "R-2024", listingId: "l4", driverId: "d9", unitId: "l4-u1",
    start: "2026-10-01", end: "2026-10-29", createdAt: "2026-09-19T12:11", status: "active",
    history: [
      { status: "pending", at: "2026-09-19T12:11", by: "Driver" },
      { status: "confirmed", at: "2026-09-20T09:30", by: "VTC Mobility Paris" },
      { status: "active", at: "2026-10-01T08:55", by: "VTC Mobility Paris" },
    ],
  }),
  booking({
    id: "R-1987", listingId: "l5", driverId: "d10", unitId: "l5-u1",
    start: "2026-08-03", end: "2026-08-31", createdAt: "2026-07-25T18:22", status: "completed",
    history: [
      { status: "pending", at: "2026-07-25T18:22", by: "Driver" },
      { status: "confirmed", at: "2026-07-26T09:12", by: "VTC Mobility Paris" },
      { status: "active", at: "2026-08-03T09:00", by: "VTC Mobility Paris" },
      { status: "completed", at: "2026-08-31T18:40", by: "VTC Mobility Paris" },
    ],
  }),
  booking({
    id: "R-1995", listingId: "l3", driverId: "d1", unitId: null,
    start: "2026-09-01", end: "2026-09-29", createdAt: "2026-08-20T08:05", status: "declined",
    history: [
      { status: "pending", at: "2026-08-20T08:05", by: "Driver" },
      { status: "declined", at: "2026-08-20T17:30", by: "VTC Mobility Paris" },
    ],
  }),
];

export const DEMO_COMPANY: CompanyState = {
  profile: {
    name: "VTC Mobility Paris",
    siret: "85234719800027",
    vat: "FR42852347198",
    address: "48 rue de la Roquette",
    city: "Paris",
    postalCode: "75011",
    region: "Île-de-France",
    country: "France",
    firstName: "Samir",
    lastName: "Haddou",
    contact: "Samir Haddou",
    phone: "+33 1 84 60 21 90",
    email: "contact@vtcmobility.fr",
    verification: "verified",
    docs: COMPANY_DOC_KEYS.map((key) => ({ key, status: "approved" as const })),
    ownerType: "company",
    businessType: "vtc-rental",
  },
  listings: LISTINGS,
  units: UNITS,
  drivers: DRIVERS,
  bookings: BOOKINGS,
  subscription: { planId: "pro", status: "active", renewal: "2026-11-01", card: "Visa •••• 5310" },
  invoices: [
    { date: "2026-09-01", number: "F-2026-0914", amount: 34.8, status: "Paid" },
    { date: "2026-08-01", number: "F-2026-0811", amount: 34.8, status: "Paid" },
    { date: "2026-07-01", number: "F-2026-0706", amount: 34.8, status: "Paid" },
  ],
};

/** A company that has just signed up: nothing yet, documents under review. */
export function newCompany(name: string, email: string): CompanyState {
  return {
    profile: {
      name,
      siret: "",
      vat: "",
      address: "",
      city: "",
      postalCode: "",
      region: "",
      country: "",
      firstName: "",
      lastName: "",
      contact: "",
      phone: "",
      email,
      verification: "incomplete",
      docs: COMPANY_DOC_KEYS.map((key) => ({ key, status: "missing" as const })),
      ownerType: "company",
      businessType: "",
    },
    listings: [],
    units: [],
    drivers: [],
    bookings: [],
    subscription: null,
    invoices: [],
  };
}
